import { ChangeDetectionStrategy, Component, computed, input, output, signal, ViewEncapsulation } from '@angular/core';
import { KeyValuePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { Input } from '@zag/ui';
import { PermissionChecklistGroupComponent, type PermissionChecklistItem } from './permission-checklist-group.component';
import {
  applyGroupScopeChange,
  applyScopeChange,
  type PermissionScopeChange,
  type PermissionScopeSelection,
  type PermissionScopeState,
} from './permission-scope-order';

export type { PermissionChecklistItem } from './permission-checklist-group.component';

function isSubsequence(needle: string, haystack: string): boolean {
  if (!needle) return true;
  let j = 0;
  for (const ch of haystack) {
    if (ch === needle[j]) {
      j++;
      if (j === needle.length) return true;
    }
  }
  return false;
}

function tokenMatches(token: string, haystacks: string[]): boolean {
  const q = token.toLowerCase();
  return haystacks.some(h => {
    const t = h.toLowerCase();
    return t.includes(q) || isSubsequence(q, t);
  });
}

function permissionMatches(query: string, perm: PermissionChecklistItem, resource: string, action: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystacks = [perm.id, perm.description ?? '', resource, action, `${resource} ${action}`, `${perm.id} ${perm.description ?? ''}`];
  return q
    .split(/\s+/)
    .filter(Boolean)
    .every(token => tokenMatches(token, haystacks));
}

@Component({
  selector: 'sc-permission-checklist',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [KeyValuePipe, TranslatePipe, Input, PermissionChecklistGroupComponent],
  host: {
    '[class.sc-permission-checklist]': 'true',
  },
  template: `
    <zag-input
      id="sc-permission-checklist-search"
      type="search"
      [value]="searchQuery()"
      (valueChange)="searchQuery.set($event)"
      [placeholder]="'permissionChecklist.searchPlaceholder' | translate"
      [clearAriaLabel]="'common.clear' | translate"
    />

    <div class="sc-permission-checklist__groups">
      @for (resource of permissionsByResource() | keyvalue; track resource.key) {
        <sc-permission-checklist-group
          [resource]="resource.key"
          [permissions]="resource.value"
          [selectedIds]="selectedIds()"
          [selectedScopes]="selectedScopes()"
          [isSearching]="isSearching()"
          (permissionToggle)="toggle($event)"
          (scopeChange)="onScopeChange($event)"
          (groupToggle)="toggleGroup(resource.value)"
          (groupScopeChange)="onGroupScopeChange(resource.value, $event)"
        />
      } @empty {
        <p class="sc-permission-checklist__empty">{{ 'common.noResults' | translate }}</p>
      }
    </div>
  `,
  styles: [
    `
      .sc-permission-checklist {
        display: block;
      }

      .sc-permission-checklist__groups {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        align-items: start;
        gap: var(--spacing-md);
        margin-top: var(--spacing-md);
      }

      .sc-permission-checklist-group .zag-accordion-item {
        background: var(--surface-color-tertiary);
        box-shadow: var(--shadow-md);
        overflow: hidden;
      }

      .sc-permission-checklist-group .zag-accordion-item__toggle:hover {
        background-color: var(--surface-color-active);
      }

      .sc-permission-checklist__empty {
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
      }
    `,
  ],
})
export class PermissionChecklistComponent {
  /** Full catalog of permissions to render. */
  readonly permissions = input.required<PermissionChecklistItem[]>();

  /** Currently checked permission ids (legacy boolean mode). */
  readonly selectedIds = input<Set<string>>(new Set());

  /** Pending scope grants; presence switches the checklist into tri-state mode. */
  readonly selectedScopes = input<PermissionScopeSelection | undefined>(undefined);

  /** Emitted with the new Set of selected ids whenever the user toggles a permission or group. */
  readonly selectedIdsChange = output<Set<string>>();

  /** Emitted with the propagated scope Map whenever the user changes a scope or group. */
  readonly selectedScopesChange = output<PermissionScopeSelection>();

  /** Search filter (optional — set externally to filter the list). */
  readonly filter = input<string>('');

  /** Internal search query from the search input on top of the list. */
  protected readonly searchQuery = signal('');

  /** Effective query combining the external filter and the internal search. */
  protected readonly effectiveQuery = computed(() => `${this.filter()} ${this.searchQuery()}`.trim());

  /** Whether a search query is active. */
  protected readonly isSearching = computed(() => this.effectiveQuery().length > 0);

  /** Permissions grouped by resource prefix, optionally filtered. */
  readonly permissionsByResource = computed(() => {
    const q = this.effectiveQuery();
    let perms = this.permissions();
    if (q.trim()) {
      perms = perms.filter(p => {
        const [resource, ...rest] = p.id.split(':');
        return permissionMatches(q, p, resource, rest.join(':'));
      });
    }
    const grouped: Record<string, PermissionChecklistItem[]> = {};
    for (const perm of perms) {
      const [resource] = perm.id.split(':');
      if (!grouped[resource]) {
        grouped[resource] = [];
      }
      grouped[resource].push(perm);
    }
    return grouped;
  });

  /** Count of selected permissions. */
  readonly selectedCount = computed(() => this.selectedScopes()?.size ?? this.selectedIds().size);

  protected toggle(id: string): void {
    const current = new Set(this.selectedIds());
    if (current.has(id)) {
      current.delete(id);
    } else {
      current.add(id);
    }
    this.selectedIdsChange.emit(current);
  }

  protected toggleGroup(perms: PermissionChecklistItem[]): void {
    const current = new Set(this.selectedIds());
    const allEnabled = perms.length > 0 && perms.every(p => current.has(p.id));
    if (allEnabled) {
      for (const p of perms) current.delete(p.id);
    } else {
      for (const p of perms) current.add(p.id);
    }
    this.selectedIdsChange.emit(current);
  }

  protected onScopeChange(change: PermissionScopeChange): void {
    this.selectedScopesChange.emit(applyScopeChange(this.selectedScopes() ?? new Map(), this.permissions(), change.id, change.scope));
  }

  protected onGroupScopeChange(perms: PermissionChecklistItem[], target: PermissionScopeState): void {
    this.selectedScopesChange.emit(
      applyGroupScopeChange(
        this.selectedScopes() ?? new Map(),
        this.permissions(),
        perms.map(p => p.id),
        target
      )
    );
  }
}
